"use client";

import React from "react";
import {
  Button,
  Sheet,
  SheetTrigger,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from "@oreme/ui";
import { CircleDot } from "lucide-react";
import { MissionType, SiteType } from "@/types";
import { round } from "@/utils";
import { useSite } from "@/hooks";
import { useTranslations } from "next-intl";
import { MissionTable, SiteMap, SiteStat } from "@/components";
import { decimalToSexagesimal } from "geolib";

type SiteSheetProps = {
  site: SiteType;
  missions?: MissionType[];
};

function SiteSheet({ site, missions = [] }: Readonly<SiteSheetProps>) {
  const t = useTranslations("site.SiteSheet");
  const currentSite = useSite();
  if (currentSite.site && currentSite.site !== site)
    site = currentSite.site as SiteType;

  const siteMissions = currentSite.getMissions(site, missions);

  return (
    <Sheet>
      <SheetTrigger asChild>
        <Button size="sm" className="w-full">
          {t("learnMore")}
        </Button>
      </SheetTrigger>
      <SheetContent className="min-w-full min-h-full lg:min-w-[960px] overflow-y-auto">
        <SheetHeader>
          <SheetTitle className="inline-flex gap-2 items-center text-2xl font-semibold">
            <CircleDot size={20} />
            {t("site")} {site.site}
          </SheetTitle>
          {site.country ? (
            <SheetDescription className="text-left">
              {site.country}
            </SheetDescription>
          ) : null}
        </SheetHeader>
        <div className="flex flex-col gap-8 p-4">
          <section className="grid grid-cols-12 gap-4">
            <SiteStat
              label="Latitude"
              value1={round(site.latitude, 2)}
              value2={decimalToSexagesimal(site.latitude)}
            />
            <SiteStat
              label="Longitude"
              value1={round(site.longitude, 2)}
              value2={decimalToSexagesimal(site.longitude)}
            />
            <SiteStat
              label="Altitude"
              value1={`${round(site.altitude, 0)} m`}
            />
          </section>
          <section className="-mt-4 h-80 border rounded overflow-hidden">
            <SiteMap site={site} />
          </section>
          {siteMissions && (
            <section>
              <header className="flex flex-col space-y-2">
                <h3 className="text-md font-semibold text-foreground mb-4">
                  {t("missions")}
                </h3>
              </header>
              <div className="border rounded overflow-hidden">
                <MissionTable data={siteMissions} />
              </div>
            </section>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}

export { SiteSheet };
